/**
 * Environment-based Configuration Manager
 * Best practice for React applications - uses build-time environment variables
 * No AWS credentials needed, fast loading, secure
 */

// Helper function to parse boolean values
const parseBoolean = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};

// Helper function to parse integer values
const parseInteger = (value, defaultValue = 0) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Helper function to parse float values
const parseFloatValue = (value, defaultValue = 0.0) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Application Configuration
 * All values are loaded from environment variables at build time
 */
const config = {
  // API Configuration
  api: {
    endpoint: process.env.REACT_APP_API_ENDPOINT || 'https://x14bncspba.execute-api.ap-southeast-2.amazonaws.com/prod',
    region: process.env.REACT_APP_API_REGION || 'ap-southeast-2',
    timeout: parseInteger(process.env.REACT_APP_API_TIMEOUT, 60000), // Increased to 60s for Bedrock processing
    retryCount: parseInteger(process.env.REACT_APP_API_RETRY_COUNT, 2), // Reduced retries for faster feedback
    retryDelay: parseInteger(process.env.REACT_APP_API_RETRY_DELAY, 2000), // Increased delay between retries
    // Phase 999.100 W2 - POST /support/request-contact (Pattern A + B). Empty
    // string until the operator wires the API GW route; supportRequestService
    // falls back to `${api.endpoint}/support/request-contact` in that case.
    supportRequest: process.env.REACT_APP_SUPPORT_REQUEST_ENDPOINT || ''
  },

  // AWS Cognito Configuration
  cognito: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'ap-southeast-2_cJFAMPlBn',
    userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '6ofh0jvh5rb8qoh2neuj160tfl',
    domain: process.env.REACT_APP_COGNITO_DOMAIN || 'afterservice.auth.ap-southeast-2.amazoncognito.com',
    region: process.env.REACT_APP_COGNITO_REGION || 'ap-southeast-2'
  },

  // Bedrock Agent Configuration
  bedrock: {
    // Phase 999.109/110: supervisor OXPOZJRDSM + alias deleted in AWS.
    // chat-handler-v9 + advocate-handler-v2 read the AgentCore runtime
    // ARN from SSM, not these headers. Fallback neutralized to ''.
    agentId: process.env.REACT_APP_BEDROCK_AGENT_ID || '',
    agentAlias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || '',
    modelId: process.env.REACT_APP_BEDROCK_MODEL_ID || 'anthropic.claude-3-sonnet-20240229-v1:0',
    maxTokens: parseInteger(process.env.REACT_APP_BEDROCK_MAX_TOKENS, 4096),
    temperature: parseFloatValue(process.env.REACT_APP_BEDROCK_TEMPERATURE, 0.7)
  },

  // Feature Flags
  features: {
    enableFeedback: parseBoolean(process.env.REACT_APP_ENABLE_FEEDBACK ?? 'true'),
    enableFileUpload: parseBoolean(process.env.REACT_APP_ENABLE_FILE_UPLOAD ?? 'false'),
    enableSessionRating: parseBoolean(process.env.REACT_APP_ENABLE_SESSION_RATING ?? 'true'),
    enableBugReporting: parseBoolean(process.env.REACT_APP_ENABLE_BUG_REPORTING ?? 'true'),
    // /transition rail+mission-brief integration. Default OFF everywhere;
    // build-time env overrides (.env.development / .env.production) flip it.
    'timeline.rail': parseBoolean(process.env.REACT_APP_FEATURE_TIMELINE_RAIL ?? 'false'),
    // Phase 999.100 W1 - regulatory launch-readiness flags. All default false.
    // SSM is canonical at /afterservice/feature-flags/*. Frontend reads these
    // build-time env vars; deploy script syncs from SSM before frontend build.
    // See .planning/phases/999.100-web-v1-launch-readiness/999.100-01-PLAN.md.
    enableK10: parseBoolean(process.env.REACT_APP_ENABLE_K10 ?? 'false'),
    enableSafetynetTriage: parseBoolean(process.env.REACT_APP_ENABLE_SAFETYNET_TRIAGE ?? 'false'),
    enableRiskFlagDispatch: parseBoolean(process.env.REACT_APP_ENABLE_RISK_FLAG_DISPATCH ?? 'false'),
    enableCrisisTierLabels: parseBoolean(process.env.REACT_APP_ENABLE_CRISIS_TIER_LABELS ?? 'false')
  },

  // Monitoring Configuration
  monitoring: {
    enableXRay: parseBoolean(process.env.REACT_APP_ENABLE_XRAY ?? 'true'),
    logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
    enableRequestTracking: parseBoolean(process.env.REACT_APP_ENABLE_REQUEST_TRACKING ?? 'true')
  },

  // Chat Configuration
  chat: {
    maxMessageLength: parseInteger(process.env.REACT_APP_MAX_MESSAGE_LENGTH, 4000),
    minMessageLength: parseInteger(process.env.REACT_APP_MIN_MESSAGE_LENGTH, 2),
    rateLimitDelay: parseInteger(process.env.REACT_APP_RATE_LIMIT_DELAY, 1000),
    maxSessionIdLength: parseInteger(process.env.REACT_APP_MAX_SESSION_ID_LENGTH, 100)
  },

  // Theme Configuration
  theme: {
    primary: process.env.REACT_APP_THEME_PRIMARY || '#5086ed',
    secondary: process.env.REACT_APP_THEME_SECONDARY || '#FFB300',
    background: process.env.REACT_APP_THEME_BACKGROUND || '#F8F9FA',
    textPrimary: process.env.REACT_APP_THEME_TEXT_PRIMARY || '#1A1A1A',
    textSecondary: process.env.REACT_APP_THEME_TEXT_SECONDARY || '#666666',
    fontFamily: process.env.REACT_APP_THEME_FONT_FAMILY || 'Inter'
  }
};

/**
 * Configuration validation
 * Ensures all required values are present
 */
const validateConfig = () => {
  const errors = [];

  // Validate required API configuration
  if (!config.api.endpoint) {
    errors.push('API endpoint is required');
  }

  // Validate required Cognito configuration
  if (!config.cognito.userPoolId) {
    errors.push('Cognito User Pool ID is required');
  }
  if (!config.cognito.userPoolClientId) {
    errors.push('Cognito User Pool Client ID is required');
  }

  // Phase 999.109/110: V3 supervisor + alias deleted in AWS. AgentCore
  // runtime is read server-side from SSM, so the frontend no longer needs
  // an agent ID/alias. Validation block removed accordingly.

  // Phase 999.100 audit 2026-05-11 A3 - surface a warning (not a hard error)
  // if REACT_APP_SUPPORT_REQUEST_ENDPOINT is missing at PROD build time. The
  // supportRequestService falls back to `${api.endpoint}/support/request-contact`
  // which is correct only when the API GW route is co-located with the main
  // chat API. If the operator wired support to a separate API GW (or hasn't
  // wired it at all), the silent fallback would route to a misrouted URL.
  // Warning rather than error: DEV builds without this env var should still
  // function via the fallback for local testing.
  if (!config.api.supportRequest) {
    console.warn(
      '[envConfig] REACT_APP_SUPPORT_REQUEST_ENDPOINT is not set. '
      + 'Falling back to `${api.endpoint}/support/request-contact`. '
      + 'Confirm this is the correct route for the current environment '
      + '(PROD operator should set this env var explicitly before build).',
    );
  }

  if (errors.length > 0) {
    console.error('Configuration validation failed:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }

  return true;
};

/**
 * Initialize configuration
 * Validates and logs configuration status
 */
const initializeConfig = () => {
  try {
    validateConfig();
    return config;
  } catch (error) {
    console.error('❌ Configuration initialization failed:', error);
    throw error;
  }
};

// Export configuration sections
export const getApiConfig = () => config.api;
export const getCognitoConfig = () => config.cognito;
export const getBedrockConfig = () => config.bedrock;
export const getFeatureFlags = () => config.features;
export const getMonitoringConfig = () => config.monitoring;
export const getChatConfig = () => config.chat;
export const getThemeConfig = () => config.theme;

// Export full configuration
export const getConfig = () => config;

// Export initialization function
export { initializeConfig };

// Default export
export default {
  config,
  initializeConfig,
  getApiConfig,
  getCognitoConfig,
  getBedrockConfig,
  getFeatureFlags,
  getMonitoringConfig,
  getChatConfig,
  getThemeConfig,
  getConfig
}; 